import { useEffect, useState } from "react";
import { Button, Container} from "semantic-ui-react";
import { Link } from "react-router";
import { cls } from 'views/NUI/utils';
import { SEOHeaders } from "components/SEOHeaders";
import FAQ from "../../xAppLib/UIelems/FAQ";
import { usePulseRewardsPoints, readPoints } from "../../xAppLib/Hooks/usePulseRewardsPoints";
import med_model from "../../models/med_model";

const pr_img_path = "https://storage.googleapis.com/instant-med-public/priceline";
const terms = <p className="text-sm !text-is2-gray-body">Terms & Conditions apply. For full Pulse Rewards terms and conditions, please refer to <a href="https://priceline.com.au/priceline-policies/pulse-rewards" target="_blank" className="!text-is2-gray-body">www.priceline.com.au/priceline-policies/pulse-rewards</a></p>
const join_url = "https://www.mypulserewards.com.au/online/customer/signup?entry=itsc";

// consults earn their own med's points, and the "standard telehealth doctor consultation" the page
// advertises is the General Consultation - shown as 50 until its points have loaded
const GENERAL_CONSULT_MID = "DocConsCov";
const STANDARD_CONSULT_POINTS = 50;
const usePagePoints = () => {
    const { signup, first_consult } = usePulseRewardsPoints();
    const [standard_consult, setStandardConsult] = useState(STANDARD_CONSULT_POINTS);

    useEffect(() => {
        med_model.get_record(`${GENERAL_CONSULT_MID}/xtra/pulse_eligible`)
            .then(points => readPoints(points) && setStandardConsult(points))
            .catch(() => {});
    }, []);

    const fmt = n => n.toLocaleString("en-AU");
    return { signup: fmt(signup), first_consult: fmt(first_consult), standard_consult: fmt(standard_consult) };
};

const Hero = () => {
      return  <div className={cls("h-[811px] lg:h-[660px] w-full relative flex flex-col lg:flex-row bg-pr-light-purple overflow-hidden")}>
                <Container className={cls("relative !pb-8 !pt-[52px] lg:!py-16")}>
                    <>
                        <div className="flex gap-9 lg:gap-11">
                            <div>
                                <img src={`${pr_img_path}/pulse-rewards.svg`} className="w-[170px] lg:w-[286px] mb-9" alt="Pulse Rewards"/>
                                <img src={`${pr_img_path}/pulse-rewards-title.svg`} className="w-[311px] lg:w-[559px]" alt="Pulse Rewards"/>
                                <a className="ui button primary !text-is2_lg !mt-[50px] w-full md:w-fit !py-4 !bg-pr-purple" target="_blank" href={join_url}>Join Pulse Rewards today</a> 
                            </div>
                           
                        </div>
                    </>
                </Container>

                <img src={`${pr_img_path}/pulse-rewards-bg.svg`} className="w-[514px] lg:w-1/2 lg:absolute left-6 right-auto lg:right-0 lg:left-auto lg:top-11 z-0" alt="pulse rewards logo"/>
                <img src={`${pr_img_path}/pulse-rewards-header-woman.webp`} className="w-[309px] lg:w-[559px] absolute left-6 bottom-0 lg:left-[calc(50%+44px)] lg:top-5 z-2" alt="woman on app"/>
            </div>
};

const Intro = () => {
    const title = <>Pulse Rewards.<br/> Uplift your everyday.</>
    const points = usePagePoints();
    return <div className={cls("py-[52px] md:py-16 bg-white")}>
        <Container>
            <div className="!flex flex flex-col lg:flex-row lg:gap-[52px] items-center h-fit">
                <h2 className="!mb-11 text-[#272727] !text-is2_4xl lg:hidden">{title}</h2>
                <img src={`${pr_img_path}/pulse-rewards-intro.png`} className="h-[426px] lg:h-full w-full lg:w-1/2 max-h-[660px] rounded-md object-cover" />
                <div className="h-fit w-full lg:w-1/2">
                    <h2 className="!mb-[52px] text-[#272727] !text-is2_4xl hidden lg:block">{title}</h2>
                    <div className="text-is-gray-700 my-11 [&_p]:mb-7">
                        <p className="mt-0">Be part of Pulse Rewards, the loyalty experience that rewards you in life's everyday moments.</p>
                        <p>Collect points across Priceline Pharmacy, atomica, Clear Skincare, Silk Laser Clinics, Australian Skin Clinics, SiSU Health Stations and InstantScripts.</p>
                        <p>From your weekly restock to a skin confidence boost or health check-up, the points add up. Before long, those points become $5 reward vouchers to spend on your next shop* at Priceline Pharmacy or atomica. Plus, Pulse Rewards members unlock other exclusive benefits including member-only offers, goodie bags and more.</p>
                        <p>Pulse Rewards members can collect {points.signup} points* for creating an InstantScripts account, {points.first_consult} points for their first telehealth doctor consultation* and {points.standard_consult} points for one standard telehealth doctor consultation per month.*</p>
                    </div>
                    {terms}
                </div>
            </div>
        </Container>
    </div>
}

const Benefits = () => {
    const tiles = [
        {
            img: `${pr_img_path}/IS-step-1.png`,
            text: <>Sign up at: <Link to="/signup" className="text-white text-underline">www.instantscripts.com.au/signup</Link> using Google, Apple, OnePass, or your email.</>
        },
        {
            img:  `${pr_img_path}/IS-step-2.png`,
            text: "Fill in your first name and last name as it appears on your Medicare card."
        },
        {
            img:  `${pr_img_path}/IS-step-3.png`,
            text: "Continue with the form and fill out the rest of your details."
        }
    ]

    return (
        <div className="bg-is-blue text-white text-left lg:text-center py-[52px] lg:py-16">
            <Container>
                <h2 className="!text-is2_4xl !mb-3">New to InstantScripts?</h2>
                <h4 className="lg:mb-[52px] !mt-0 text-is2-blue-100">Here’s how to create an account:</h4>
                <hr className="border-t border-is2-blue-300 my-9 lg:hidden" />
                <div className="flex flex-col lg:flex-row gap-8">
                    {tiles.map((tile, i) => {
                        return (
                            <div className="flex flex-col-reverse lg:flex-col w-full lg:w-1/3" key={i}>
                                <img src={tile.img} className="w-full"/>
                                <p className="text-is2-blue-100 text-left !mb-4 lg:!mb-0 lg:mt-4 font-medium text-is2_lg">{tile.text}</p>
                            </div>
                        )
                    })}
                </div>

                <Button as={Link} basic to="/signup" className="!mt-8 w-full lg:w-fit !font-medium !text-is2_lg !py-4">Join InstantScripts</Button>
                <hr className="border-t border-is2-blue-650 my-8 lg:my-16" />
                <h3 className="mb-[52px] !text-is2_2xl">Already a Pulse Rewards member?</h3>
                <div className="flex flex-col lg:flex-row justify-center items-center gap-16 mx-auto">
                    <div className="text-is2-blue-100 text-left [&_*]:text-is2_lg">
                        <p className="mb-8">Adding your Pulse Rewards number to your InstantScripts account is simple!</p>
                        <p><Link to="/login" className="font-medium text-underline">Login</Link> to your InstantScripts account and add your Pulse Rewards member number per below.</p>
                        <Button as={Link} basic to="/login" className="!font-medium text-underline !text-is2_lg !mt-8 w-full lg:w-fit">Login</Button>
                    </div>
                    <img src={`${pr_img_path}/pulse-rewards-is-settings.png`} className="rounded-lg w-full lg:w-1/2" />
            </div>
           </Container>
        </div>
    );
}

const Plan = () => {
    const points = usePagePoints();
    const tiles = [
        {
            title: `${points.signup} Points for creating a new InstantScripts account`,
            desc: <>
                    <p>*To be eligible to receive Pulse Rewards points you must:</p>
                    <ol className="list-decimal pl-4">
                        <li>Create an InstantScripts account from 13 August 2026 12.01 AEST while holding an active Pulse Rewards membership account.</li>
                        <li>Have a Pulse Rewards membership account at the time you create your InstantScripts account.</li>
                        <li>Do not already hold an InstantScripts account as at 11:59pm on 12 August 2026. This offer does not apply to any existing InstantScripts accounts or users as at that date, and points cannot be applied retrospectively. Users cannot have multiple InstantScripts accounts. Reward is only applied once and is not redeemable multiple times.</li>
                        <li>{points.signup} points offer is exclusive to Pulse Rewards members and applies from 13 August 2026 12.01 AEST.</li>
                        <li>Participation in this offer is subject to the Pulse Rewards and InstantScripts Terms and Conditions. We reserve the right to withdraw this offer at any time.</li>
                    </ol>
                </>
        },
        {
            title:  `${points.first_consult} Points for first use of InstantScripts`,
            desc: <>
                    <p>*To be eligible to receive Pulse Rewards points you must:</p>
                    <ol className="list-decimal pl-4">
                        <li>Complete your first-ever InstantScripts telehealth doctor consultation from 13 August 2026 12.01am AEST.</li>
                        <li>Have a Pulse Rewards membership account at the time you book your consult.</li>
                        <li>Add your Pulse Rewards membership number to your InstantScripts account at the time of the transaction. This offer does not apply if your Pulse Rewards membership number isn't added to your InstantScripts account at the time of the transaction.</li>
                        <li>Have not used InstantScripts before. This offer only applies to your first InstantScripts transaction and is not available to anyone who has used InstantScripts previously.</li>
                        <li>{points.first_consult} points offer is exclusive to Pulse Rewards members, applies from 13 August 2026 12.01am AEST, and is for telehealth doctor consults only. Points cannot be applied retrospectively.</li>
                        <li>Participation in this offer is subject to the Pulse Rewards and InstantScripts Terms and Conditions. We reserve the right to withdraw this offer at any time.</li>
                    </ol>
                </>
        },
        {
            title:  `${points.standard_consult} Points for your InstantScripts telehealth consult`,
            desc: <>
                <p>*To be eligible to receive Pulse Rewards points you must:</p>
                <ol className="list-decimal pl-4">
                    <li>Hold both a Pulse Rewards membership account and an InstantScripts account at the time of booking your consult.</li>
                    <li>Add your Pulse Rewards membership number to your InstantScripts account at the time of the transaction. This offer does not apply if your Pulse Rewards membership number isn't added to your InstantScripts account at the time of the transaction.</li>
                    <li>{points.standard_consult} points offer is exclusive to Pulse Rewards members, applies from 13 August 2026 12.01am AEST, and is for telehealth doctor consults only. One offer redemption per member per calendar month.</li>
                    <li>Participation in this offer is subject to the Pulse Rewards and InstantScripts Terms and Conditions. We reserve the right to withdraw this offer at any time.</li>
                </ol>
            </>
        }
    ]
            
    return <div className="py-16 bg-is2-blue-50">
            <Container className="!flex flex-col items-center">
                <h2 className="text-left lg:text-center font-medium !mb-9 lg:!mb-[52px] !text-is2_4xl">What Pulse Rewards members get at InstantScripts</h2>
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-4 mb-[52px]">
                    {tiles.map((tile, i) => {
                        return <div key={i} className="w-full lg:w-1/3 flex flex-col gap-4">
                                <div className="bg-pr-purple rounded-lg py-6 px-8 text-white lg:min-h-[156px]">
                                    <h3 className="!text-is2_xl">{tile.title}</h3>
                                </div>
                                <div className="bg-white p-8 rounded-lg flex-1 text-is2-gray-450 !text-base">
                                    {tile.desc}
                                </div>
                        </div>
                    })}
                </div>
                <a className="ui button primary !text-is2_lg w-full md:w-fit !py-4 !bg-pr-purple !font-medium" target="_blank" href={join_url}>Join Pulse Rewards today</a>
            </Container>
        </div>
}


const Terms = () => {
    return <div className={cls("bg-is-gray-10 py-[52px] md:py-24")} id="terms">
        <Container>
            <h5 className="text-sm">Terms & Conditions</h5>
            {terms}
        </Container>
    </div>
}

const PulseRewards = () => {
     
    const faq = [
        {
            title: 'What is Pulse Rewards?',
            content: 'Pulse Rewards is the new loyalty experience replacing Sister Club, designed to give you more ways to collect points and enjoy rewards across more brands including Priceline Pharmacy, Priceline, atomica, SILK Laser Clinics, Clear Skincare, Australian Skin Clinics, InstantScripts and SiSU Health.'
        },
        {
            title: `I'm already a Sister Club member, do I need to sign up to Pulse Rewards?`,
            content: `No, you're all set. Your Sister Club membership will automatically transition to Pulse Rewards on 13 August 2026 and your current level remains the same.`
        },
        {
            title: 'How do I join Pulse Rewards?',
            content: 'You can join Pulse Rewards in-store at any  Priceline Pharmacy , Priceline or atomica store, or online at mypulserewards.com.au or priceline.com.au.'
        },
        {
            title: 'Is it free to join?',
            content: 'Yes, becoming a Pulse Rewards member is free.'
        },
        {
            title: 'Where can I collect Pulse Rewards points?',
            content: 'Pulse Rewards members can collect points with Priceline Pharmacy, Priceline, atomica. SILK Laser Clinics, Clear Skincare, Australian Skin Clinics, InstantScripts and SiSU Health.'
        },
        {
            title: 'What are the points for and how can I redeem them?',
            content: `Once you've collected 500 points, they'll automatically convert into a $5 reward voucher, which you can use on a future eligible purchase at any Priceline Pharmacy, Priceline or atomica store. Simply scan your Pulse Rewards card or sign in to your account when you shop, and any available reward vouchers will be ready to use at checkout.`
        },
        {
            title: `Can I collect points online and in-store?`,
            content: ` At Priceline Pharmacy, Priceline and atomica, points can be collected both in-store and online, by showing your member card or searching your account. For SiSU health stations in Priceline stores, if you are eligible for a Pulse Rewards offer during your health check, you will be prompted to look up and connect your Pulse Rewards account using your email address, mobile number or membership number. At SILK Laser Clinics, Clear Skincare, Australian Skin Clinics and InstantScripts, points can only be collected by entering your member number online at the time of purchasing or booking eligible services or consults.`
        },
        {
            title: `How do I collect Pulse Rewards points?`,
            content: `Points can  be collected on eligible purchases when shopping either in-store or online at Priceline Pharmacy, Priceline and atomica. Points can also be collected by entering your Pulse Rewards number when making an eligible purchase online at SILK Laser Clinics, Clear Skincare and Australian Skin Clinics,  by entering your member number when booking an eligible service or consult online at InstantScripts, and by looking up and connecting your Pulse Rewards account when completing an eligible SiSU Health check in a Priceline store.`
        },
        {
            title: `Do I collect the same number of points everywhere?`,
            content: `With Priceline Pharmacy, Priceline and atomica, you'll collect points on eligible purchases based on your membership level:
            Level 1 Explorer: 1 point per $1 spent
            Level 2 Insider: 2 points per $1 spent
            Level 3 VIP: 3 points per $1 spent
            At SILK Laser Clinics, Clear Skincare, Australian Skin Clinics, InstantScripts and SiSU Health, you can collect bonus points* on eligible services and consults.
            Visit priceline.com.au/pulse-rewards for more information on member offers across all participating brands.
            *Note: bonus points do not count towards member level spend.`
        },
        {
            title: `What are bonus points?`,
            content: `Bonus points are extra points offers to help you get to your next $5 reward voucher faster. Bonus points contribute towards reward vouchers, however they do not contribute to level spend. Bonus points are points that are collected at  SILK Laser Clinics, Clear Skincare, Australian Skin Clinics, InstantScripts, SiSU Health and across bonus points promotions such as Health Boost. `
        },
        {
            title: `Do I need to scan my card every time?`,
            content: `To ensure points are collected at at SILK Laser Clinics, Clear Skincare and Australian Skin Clinics you'll need to enter your Pulse Rewards number at the time of purchase. For InstantScripts you can either enter your Pulse Rewards number to your account, or enter your number at the time of booking a service or consult online. For SiSU, you'll need to look up and connect your Pulse Rewards account during an eligible SiSU Health Check. At Priceline Pharmacy, Priceline and atomica, members need to scan their digital Pulse Rewards card or physical Sister Club card when purchasing in-store or must be logged in to their Pulse Rewards account when shopping online.`
        },
        {
            title: `Where can I see my points balance?`,
            content: `You can check your Pulse Rewards points balance in a few ways:
            1. Log in to your Pulse Rewards account on the Priceline website or app. Your current points balance is displayed in your Pulse Rewards account.
            2. Store team members at any Priceline Pharmacy, Priceline & atomica can tell you your current points balance by scanning your digital or physical Pulse Rewards card in store, or they can look up your details in the system.
            3. Check your emails from Priceline. Your points balance is often included in member communications and promotional emails.
            4. Check your receipt - your points balance will be visible on any Priceline Pharmacy, Priceline & atomica receipt.
            5. Contact our Customer Support team on 1300 88 44 11 or email info@priceline.com.au`
        },
        {
            title: `How long does it take for the points to appear in my account?`,
            content: `Points collected at Priceline Pharmacy, Priceline and atomica will show immediately in your Pulse Rewards account balance after purchase, although some points promotions collected at Priceline Pharmacy, Priceline and atomica may take up to 6 weeks to appear in your account. See promotion T&Cs for further details. Points collected at SILK Laser Clinics, Clear Skincare, Australian Skin Clinics, InstantScripts and SiSU Health may take up to 6 weeks to appear in your account following service completion. If you are experiencing any issues, please contact our Customer Support team on 1300 88 44 11 or email info@priceline.com.au. You may need to have your receipt or order number handy so that any missing points can be added to your account.`
        },
        {
            title: `What do I do if I have not received my Pulse Rewards points?`,
            content: `Please contact our Customer Support team on 1300 88 44 11 or email info@priceline.com.au for assistance. You may need to have your receipt or order number handy so that any missing points can be added to your account.`
        },
        {
            title: `Can I transfer points to someone else?`,
            content: `No, Pulse Rewards points are not transferrable to other members.`
        },
        {
            title: `Where can I redeem my $5 reward voucher?`,
            content: `Once you've collected 400 points (500 points effective 1 October 2026), they'll automatically convert into a $5 reward voucher, which you can use on a future eligible purchase, in-store or online at Priceline Pharmacy, Priceline or atomica.`
        },
        {
            title: `Do points or vouchers expire?`,
            content: `Pulse Rewards points will expire 12 months after the date at which they were credited to a members account. Pulse Rewards members who are Level 2 Insider or Level 3 VIP, will not have their Pulse Rewards points expire. Vouchers expire 3 months from the date they were credited to a members account, regardless of the members level.`
        },
        {
            title: `Can I still use my existing Sister Club card?`,
            content: `Yes, your Sister Club card remains valid. Although physical Sister Club cards will not be replaced with physical Pulse Rewards cards, you can always download your digital member card!
            To access your Pulse Rewards digital card:
            1. Simply log in to your Pulse Rewards account (previously Sister Club account) via the Priceline website or app on your mobile device.  
            2. Open your member card within your account.
            3. Select Add to Apple Wallet or Add to Google Wallet.
            4. Save your digital card to your phone's wallet.
            5. Scan your digital card in-store whenever you shop to collect points and access member offers.`
        },
        {
            title: `Will my digital member card still work?`,
            content: `Yes! Your existing Sister Club card will automatically transition to a Pulse Rewards digital card. You don't need to do anything, simply keep using your digital card when you shop to collect points, access member offers and redeem reward vouchers.`
        },
        {
            title: `Can I access a digital Sister Club member card on the Priceline App?`,
            content: `Yes, you can access a digital member card when you download the Priceline Shopping App. The digital card is accessed within the app and provides a barcode that is ready for you to present in store at checkout to scan. You can also add your member card to your Apple or Google wallet from the Priceline Shopping App. 
            To access a digital card to add to your mobile wallet, log in online and head to your Account Summary or download the Priceline App. Further details can be found here: https://www.priceline.com.au/pulse-rewards-digital-wallet`
        },
        {
            title: `What are the different membership levels?`,
            content: `Pulse Rewards has three membership levels: Level 1 Explorer, Level 2 Insider and Level 3 VIP.
            You'll start as a Level 1 Explorer when you join. As you shop and collect points, you can progress through the levels to unlock even more rewards and exclusive member benefits. No matter which level you're in, you'll enjoy great value, with more benefits available as you move up.`
        },
        {
            title: `What is member level spend and how is it calculated?`,
            content: `Pulse Rewards members will need to accrue member level spend to move up Pulse Rewards membership levels. All Pulse Rewards members will collect 1 Level Spend Point per eligible dollar spent in-store and online at Priceline. The number of Level Spend accrued over a 12 month period will determine the member’s Pulse Rewards Level status:

            a) Level 1 Explorer: 0 - $399 Level Spend
            b) Level 2 Insider: between 400 - $799 Level Spend
            c) Level 3 VIP: $800 or more Level Spend`
        },
        {
            title: `Can team members at SILK Laser Clinics, Clear Skincare, Australian Skin Clinics or InstantScripts tell me my points balance?`,
            content: `Not yet. You can currently collect Pulse Rewards points online with SILK Laser Clinics, Clear Skincare, Australian Skin Clinics, InstantScripts and SiSU Health, but team members at these locations can't view your Pulse Rewards points balance. We're working on adding this feature in the future. For information on your points balance, please contact contact our Customer Support team on 1300 88 44 11 or email info@priceline.com.au for assistance.`
        },
        {
            title: `Can I sign up or scan my Pulse Rewards card in clinic at SILK Laser Clinics, Clear Skincare or Australian Skin Clinics?`,
            content: `Not yet. You can currently collect Pulse Rewards points on eligible online services and consults with SILK Laser Clinics, Clear Skincare and Australian Skin Clinics. At the moment, team members at these locations can't scan Pulse Rewards cards in person. We're working on adding this feature, so you'll be able to collect points in-clinic in the future.`
        },
        {
            title: `Who should I contact with any other questions about Pulse Rewards?`,
            content: `Please contact our Customer Support team on 1300 88 44 11 or email info@priceline.com.au for assistance.`
        },
        {
            title: `What if I accidentally signed up twice as a Pulse Rewards member?`,
            content: <>Email our <a href="https://www.priceline.com.au/contact-us" target="_blank">Customer Service Team</a> info@priceline.com.au at any time or call them on 1300 88 44 11 between 9.00am to 5.00pm (AEST), Monday to Friday. They will arrange to merge your duplicate Pulse Rewards cards, to prevent losing your points balance.</>
        },
        {
            title: `How do I subscribe or unsubscribe from emails?`,
            content: `To receive emails from Priceline Pharmacy you'll need to be a Pulse Rewards member. To unsubscribe from Priceline Pharmacy communications, follow the link on the email you receive. You can also manage your preferences when you log in online and click 'Manage Communication Preferences'.`
        },
        {
            title: `What do I do if I forget my account password?`,
            content: <>Simply <a href="https://www.priceline.com.au/login/forgot-password" target="_blank">click here </a>and you will be directed to a page where you enter your email address. Click continue, then follow the prompts to receive an email that includes a link and simple instructions on how to reset your password.</>
        },
        {
            title: `How do I change my password?`,
            content: <>Log in to your Pulse Rewards account <a href="https://www.priceline.com.au/login" target="_blank">here</a>, and click on the 'Manage Account' tab located to the left of the page. Click 'change password' heading. Complete the required details then click 'Save'.</>
        },
        {
            title: `Where can I find my Pulse Rewards number?`,
            content: `There's a few ways to locate your member number:
            1. Check your email - any email you receive from Pulse Rewards, Priceline Pharmacy, Priceline or atomica will include your Pulse Rewards member number.
            2. Log in to your account via the Priceline website or app to view your member details.
            3. Contact the Customer Support team on 1300 88 44 11 or email info@priceline.com.au who will be able to assist locating your member number once they've completed an identity verification.`
        },
        
    ]

	return(
        <>
          <SEOHeaders
            title="Pulse Rewards | InstantScripts"
            path={'/pulse-rewards'}
            index={true}
            follow={true}
        />
          <div className='pulse-rewards bg-white'>
            <Hero />
            <Intro />
            <Plan />
            <Benefits />
            <FAQ headerAs='h2' data={faq} hide_warn readmore={false} clsn={"bg-is2-blue-50"} faq_cls="bg-white"/>
            <Terms />
	        </div>
        </>
      
    )  
  
};

export default PulseRewards;
